import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Steps } from '@/components/ui/steps'
import { useToast } from '@/components/ui/toast'
import api from '@/lib/api'
import { cn, formatBRL, formatDate } from '@/lib/utils'
import { AlertTriangle, ArrowRight, CheckCircle2, Circle, Eye, EyeOff, Loader2, Lock, Send, XCircle } from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { IoAlertCircleOutline } from 'react-icons/io5'
import { statusMeta } from './nfStatus'
import ValorDivergente, { valorDivergente } from './ValorDivergente'

const STEPS = [
  { key: 'lista', label: 'Selecionar' },
  { key: 'senha', label: 'Senha' },
  { key: 'processando', label: 'Emitindo' },
  { key: 'resultado', label: 'Resultado' },
]

/** Etapas do processo de emissão exibidas durante o loading (ordem real do backend). */
const ETAPAS = [
  'Montando a DPS (Declaração de Prestação de Serviços)',
  'Assinando digitalmente com o certificado A1',
  'Validando o XML no schema da SEFIN Nacional',
  'Enviando à SEFIN Nacional (mTLS)',
  'Processando retorno e gerando XML/PDF',
  'Atualizando o status das notas',
]

function hoje() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function GerarNfModal({ open, onClose, onDone }) {
  const toast = useToast()
  const [step, setStep] = useState('lista') // lista | senha | processando | resultado
  const [nfs, setNfs] = useState([])
  const [marcados, setMarcados] = useState({})
  const [dataCompetencia, setDataCompetencia] = useState(hoje())
  const [aliquota, setAliquota] = useState('')
  const [senha, setSenha] = useState('')
  const [senhaVisivel, setSenhaVisivel] = useState(false)
  const [etapa, setEtapa] = useState(0)
  const [resultados, setResultados] = useState([])
  const [erroGeral, setErroGeral] = useState('')
  const [loading, setLoading] = useState(false)
  const etapaTimer = useRef(null)

  useEffect(() => {
    if (!open) return
    setStep('lista'); setSenha(''); setSenhaVisivel(false); setResultados([]); setErroGeral(''); setEtapa(0)
    setDataCompetencia(hoje())
    ;(async () => {
      setLoading(true)
      try {
        const [nfRes, paramRes] = await Promise.all([
          api.get('/nfs', { params: { status: 'rascunho,verificado', limit: 100 } }),
          api.get('/parametro'),
        ])
        setNfs(nfRes.data.data)
        setMarcados(Object.fromEntries(nfRes.data.data.map((n) => [n.nfID, true])))
        setAliquota(String(paramRes.data.aliquotaImposto ?? ''))
      } finally {
        setLoading(false)
      }
    })()
  }, [open])

  // Avança as etapas do loading até a última e permanece nela até o retorno.
  useEffect(() => {
    if (step !== 'processando') {
      if (etapaTimer.current) clearInterval(etapaTimer.current)
      return
    }
    etapaTimer.current = setInterval(() => {
      setEtapa((e) => Math.min(e + 1, ETAPAS.length - 1))
    }, 1600)
    return () => etapaTimer.current && clearInterval(etapaTimer.current)
  }, [step])

  const selecionados = nfs.filter((n) => marcados[n.nfID])
  const todosMarcados = nfs.length > 0 && selecionados.length === nfs.length
  // Último aviso antes de emitir: NFs cujo valor difere do contratado.
  const divergentes = selecionados.filter((n) => valorDivergente(n))

  function alternarTodos() {
    const novo = !todosMarcados
    setMarcados(Object.fromEntries(nfs.map((n) => [n.nfID, novo])))
  }

  async function emitir() {
    if (senha === '') {
      toast.error('Informe a senha do certificado.')
      return
    }
    setEtapa(0)
    setErroGeral('')
    setStep('processando')
    try {
      const { data } = await api.post('/nfs/emitir', {
        nfIDs: selecionados.map((n) => n.nfID),
        senha,
        aliquotaImposto: Number(aliquota) || 0,
        dataCompetencia,
      })
      setResultados(data.resultados || [])
    } catch (err) {
      // Falha global (senha do certificado, rede, 5xx): também vai para a etapa
      // de resultado, com a mensagem do backend.
      setResultados([])
      setErroGeral(err?.response?.data?.error || 'Falha ao emitir o lote. Verifique a senha do certificado e tente novamente.')
    } finally {
      setSenha('')
      setSenhaVisivel(false)
      setStep('resultado')
      onDone() // recarrega a listagem por trás, sem fechar o modal
    }
  }

  const okCount = resultados.filter((r) => r.status === 'emitido').length
  const errCount = resultados.length - okCount
  const nfPorId = Object.fromEntries(nfs.map((n) => [n.nfID, n]))

  // Footer por etapa.
  let footer = null
  if (step === 'lista') {
    footer = (
      <>
        <Button variant="outline" onClick={onClose}>Fechar</Button>
        <Button onClick={() => setStep('senha')} disabled={selecionados.length === 0} className="bg-chart-2">
          <ArrowRight className="h-4 w-4" /> Gerar NF’s ({selecionados.length})
        </Button>
      </>
    )
  } else if (step === 'senha') {
    footer = (
      <>
        <Button variant="outline" onClick={() => setStep('lista')}>Voltar</Button>
        <Button onClick={emitir} className="bg-chart-2" disabled={senha === ''} ><Send className="h-4 w-4" /> Confirmar e emitir</Button>
      </>
    )
  } else if (step === 'resultado') {
    footer = (
      <>
        {(erroGeral || errCount > 0) && (
          <Button variant="outline" onClick={() => { setStep('senha'); setErroGeral('') }}>
            Tentar novamente
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
      </>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={step === 'processando' ? undefined : onClose}
      title="Gerar NF em lote"
      description={step === 'lista' ? 'Emite as NFs em rascunho/verificado selecionadas.' : undefined}
      size="xl"
      footer={footer}
    >
      <Steps steps={STEPS} current={step} />

      {step === 'lista' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Data de competência</Label>
              <Input type="date" value={dataCompetencia} onChange={(e) => setDataCompetencia(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Alíquota do imposto (%)</Label>
              <Input type="number" step="0.01" value={aliquota} onChange={(e) => setAliquota(e.target.value)} />
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Carregando NFs...</p>
          ) : nfs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Nenhuma NF em rascunho ou verificado.</p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="max-h-[55vh] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-muted/50 sticky top-0">
                    <tr>
                      <th className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={todosMarcados}
                          onChange={alternarTodos}
                          title={todosMarcados ? 'Desmarcar todas' : 'Marcar todas'}
                          className="h-4 w-4 align-middle accent-[var(--primary)]"
                        />
                      </th>
                      <th className="px-3 py-2 font-medium">Cliente</th>
                      <th className="px-3 py-2 font-medium">Contrato</th>
                      <th className="px-3 py-2 font-medium text-right">Valor</th>
                      <th className="px-3 py-2 font-medium text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nfs.map((n) => {
                      const m = statusMeta(n.status)
                      return (
                        <Fragment key={n.nfID}>
                          <tr className="border-t border-border/60">
                            <td className="px-3 pt-2 align-top">
                              <input
                                type="checkbox"
                                checked={!!marcados[n.nfID]}
                                onChange={(e) => setMarcados((mk) => ({ ...mk, [n.nfID]: e.target.checked }))}
                                className="h-4 w-4 align-middle accent-[var(--primary)]"
                              />
                            </td>
                            <td className="px-3 pt-2 align-top font-medium">{n.clienteNome}</td>
                            <td className="px-3 pt-2 align-top whitespace-nowrap text-muted-foreground">{n.numeroContrato}</td>
                            <td className="px-3 pt-2 align-top text-right whitespace-nowrap tabular-nums">
                              <span className="inline-flex items-center justify-end gap-1">
                                {formatBRL(n.valor)}
                                <ValorDivergente status={n.status} valor={n.valor} valorContratado={n.valorContratado} />
                              </span>
                            </td>
                            <td className="px-3 pt-2 align-top text-center"><Badge variant={m.variant}>{m.label}</Badge></td>
                          </tr>
                          <tr>
                            <td></td>
                            <td colSpan={4} className="px-3 pb-2 pt-0.5 text-xs text-muted-foreground/80 whitespace-pre-line break-words">
                              {n.descricaoServico}
                            </td>
                          </tr>
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-border bg-muted/30 text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>
                  {selecionados.length} de {nfs.length} selecionada(s) ·{' '}
                  <span className="tabular-nums">{formatBRL(selecionados.reduce((s, n) => s + Number(n.valor || 0), 0))}</span>
                </span>
                {divergentes.length > 0 && (
                  <span className="flex items-center gap-1 text-yellow-600">
                    <IoAlertCircleOutline className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {divergentes.length} com valor diferente do contratado
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {step === 'senha' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Você está prestes a emitir <b className="text-foreground">{selecionados.length}</b> nota(s) fiscal(is) com competência <b className="text-foreground">{formatDate(dataCompetencia)}</b> e alíquota <b className="text-foreground">{aliquota}%</b>.
          </p>

          {/* Última chance de rever valores antes da emissão (irreversível). */}
          {divergentes.length > 0 && (
            <div className="rounded-lg border border-yellow-600/40 bg-yellow-600/10 p-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-yellow-700 dark:text-yellow-500">
                <IoAlertCircleOutline className="h-4 w-4 shrink-0" aria-hidden="true" />
                {divergentes.length} nota(s) com valor diferente do contratado
              </p>
              <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground max-h-40 overflow-auto">
                {divergentes.map((n) => (
                  <li key={n.nfID} className="flex flex-wrap items-baseline gap-x-1.5">
                    <span className="text-foreground">{n.clienteNome}</span>
                    <span>· contrato {n.numeroContrato}</span>
                    <span className="tabular-nums">· NF {formatBRL(n.valor)}</span>
                    <span className="tabular-nums">· contratado {formatBRL(n.valorContratado)}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs text-muted-foreground">
                Volte e ajuste os valores se necessário — depois de emitida, a NF só pode ser cancelada ou substituída.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Senha do certificado digital (A1)</Label>
            <div className="relative">
              <Input
                type={senhaVisivel ? 'text' : 'password'}
                autoFocus
                name="senha-certificado"
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                placeholder="••••••••"
                onKeyDown={(e) => e.key === 'Enter' && emitir()}
                className="pr-10"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setSenhaVisivel((v) => !v)}
                title={senhaVisivel ? 'Ocultar senha' : 'Mostrar senha'}
                aria-label={senhaVisivel ? 'Ocultar senha' : 'Mostrar senha'}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors cursor-pointer opacity-50 hover:opacity-100"
              >
                {senhaVisivel ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">A senha é usada apenas para assinar este lote e não é armazenada.</p>
          </div>
        </div>
      )}

      {step === 'processando' && (
        <div className="py-4">
          <div className="flex flex-col items-center text-center gap-1 mb-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
            <p className="font-medium">Emitindo {selecionados.length} nota(s) fiscal(is)...</p>
            <p className="text-xs text-muted-foreground">Não feche esta janela. Cada nota é processada individualmente.</p>
          </div>

          <div className="h-1.5 w-full rounded-full bg-border overflow-hidden mb-5">
            <div
              className="h-full bg-primary rounded-full transition-all duration-700 ease-out"
              style={{ width: `${((etapa + 1) / ETAPAS.length) * 100}%` }}
            />
          </div>

          <ul className="space-y-2 max-w-md mx-auto">
            {ETAPAS.map((t, i) => {
              const done = i < etapa
              const atual = i === etapa
              return (
                <li key={t} className={cn('flex items-center gap-2.5 text-sm transition-colors', !done && !atual && 'text-muted-foreground/50')}>
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                  ) : atual ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0" />
                  )}
                  <span className={cn(atual && 'font-medium text-foreground', done && 'text-muted-foreground')}>{t}</span>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {step === 'resultado' && (
        <div className="space-y-4">
          {erroGeral ? (
            <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Não foi possível processar o lote</p>
                <p className="text-sm text-muted-foreground mt-1">{erroGeral}</p>
                <p className="text-xs text-muted-foreground mt-2">Nenhuma nota foi emitida. As NFs mantêm o status atual.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-2xl font-semibold tabular-nums">{okCount}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">emitida(s) com sucesso</p>
                </div>
                <div className={cn('rounded-lg border p-3', errCount > 0 ? 'border-destructive/40' : 'border-border')}>
                  <div className={cn('flex items-center gap-2', errCount > 0 ? 'text-destructive' : 'text-muted-foreground')}>
                    <XCircle className="h-4 w-4" />
                    <span className="text-2xl font-semibold tabular-nums">{errCount}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">com erro</p>
                </div>
              </div>

              <div className="rounded-lg border border-border divide-y divide-border max-h-72 overflow-auto">
                {resultados.map((r) => {
                  const nf = nfPorId[r.nfID]
                  const ok = r.status === 'emitido'
                  return (
                    <div key={r.nfID} className="flex items-start gap-3 p-3">
                      {ok
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                        : <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-medium text-sm">{nf?.clienteNome || `NF #${r.nfID}`}</span>
                          {nf?.numeroContrato && <span className="text-xs text-muted-foreground">contrato {nf.numeroContrato}</span>}
                          {r.numero && <span className="text-xs text-muted-foreground">· nº {r.numero}</span>}
                        </div>
                        {r.mensagem && (
                          <p className={cn('text-xs mt-0.5 break-words', ok ? 'text-muted-foreground' : 'text-destructive')}>{r.mensagem}</p>
                        )}
                      </div>
                      {nf && <span className="text-sm tabular-nums shrink-0">{formatBRL(nf.valor)}</span>}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  )
}
